import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import EditableTable from './EditableTable';

/**
 * Editor for `public.rankup_config` rows where server = selectedServer.
 * 10 rows per server (T1 → T10).
 */
const RankupTab = ({ server }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingDefaults, setCreatingDefaults] = useState(false);

  const load = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('rankup_config')
      .select('*')
      .eq('server', server)
      .order('tier', { ascending: true });
    if (dbError) setError(dbError.message);
    setRows(data || []);
    setLoading(false);
  }, [server]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'tier',
      label: t('superadmin.businessRules.rankup.tier', 'Tier'),
      type: 'number',
      width: '80px',
      readonly: true,
      render: (v) => (
        <span className="font-data font-semibold text-foreground">T{v}</span>
      ),
    },
    {
      key: 'min_diamonds',
      label: t('superadmin.businessRules.rankup.minDiamonds', 'Min diamonds'),
      type: 'bigint',
    },
    {
      key: 'ratio_rankup',
      label: t('superadmin.businessRules.rankup.ratioRankup', 'Rank-up %'),
      type: 'decimal',
      width: '110px',
    },
    {
      key: 'ratio_maintain',
      label: t('superadmin.businessRules.rankup.ratioMaintain', 'Maintain %'),
      type: 'decimal',
      width: '110px',
    },
    {
      key: 'min_live_days',
      label: t('superadmin.businessRules.rankup.minLiveDays', 'Min LIVE days'),
      type: 'number',
      width: '110px',
    },
    {
      key: 'min_live_hours',
      label: t('superadmin.businessRules.rankup.minLiveHours', 'Min LIVE h'),
      type: 'decimal',
      width: '110px',
    },
    {
      key: 'tier_adj_factor',
      label: t('superadmin.businessRules.rankup.tierAdjFactor', 'Adj factor'),
      type: 'decimal',
      width: '110px',
    },
  ];

  const handleSave = useCallback(
    async (row, patch) => {
      const { error: dbError } = await supabase
        .from('rankup_config')
        .update(patch)
        .eq('id', row.id);
      if (dbError) throw dbError;
      await load();
    },
    [load]
  );

  const handleSeedDefaults = useCallback(async () => {
    if (!server) return;
    if (
      !window.confirm(
        t(
          'superadmin.businessRules.rankup.seedConfirm',
          'Create empty T1→T10 rows for this server?'
        )
      )
    )
      return;
    setCreatingDefaults(true);
    const payload = Array.from({ length: 10 }, (_, i) => ({
      server,
      tier: i + 1,
      min_diamonds: 0,
      ratio_rankup: i === 9 ? null : 0,
      ratio_maintain: i < 2 ? 0 : 0,
      min_live_days: 7,
      min_live_hours: 15,
      tier_adj_factor: 1.0,
    }));
    const { error: dbError } = await supabase
      .from('rankup_config')
      .insert(payload);
    if (dbError) {
      setError(dbError.message);
      window.alert(dbError.message);
    }
    setCreatingDefaults(false);
    await load();
  }, [server, load, t]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.rankup.intro',
          'Tier thresholds and rank-up / maintain ratios per server. Tier 10 has no rank-up ratio (top tier — maintain only).'
        )}
      </p>
      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 ? (
        <div className="rounded-2xl bg-card border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            {t(
              'superadmin.businessRules.rankup.notSeeded',
              'No tier config seeded for this server yet.'
            )}
          </p>
          <button
            type="button"
            onClick={handleSeedDefaults}
            disabled={creatingDefaults}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {creatingDefaults
              ? t('superadmin.businessRules.saving', 'Saving…')
              : t(
                  'superadmin.businessRules.rankup.seed',
                  'Create T1→T10 default rows'
                )}
          </button>
        </div>
      ) : (
        <EditableTable
          columns={columns}
          rows={rows}
          loading={loading}
          onRowSave={handleSave}
        />
      )}
    </div>
  );
};

export default RankupTab;
